// src/store/AuthContext.jsx — Production JWT auth
// SECURITY NOTE: Token stored in localStorage for SPA compatibility.
// For maximum security in production, consider migrating to httpOnly cookie
// approach with Laravel Sanctum SPA authentication (CSRF-based).
// See: https://laravel.com/docs/sanctum#spa-authentication
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'solvit_token';
const USER_KEY  = 'solvit_user';

const normalizeRole = (role) => {
  if (!role) return null;
  const r = role.toLowerCase();
  if (r === 'super_admin' || r === 'superadmin' || r === 'admin') return 'super_admin';
  if (r === 'school_admin' || r === 'school')                     return 'school_admin';
  if (r === 'teacher')                                             return 'teacher';
  return r;
};

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [token,   setToken]   = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // On mount: verify token is still valid against the backend
  useEffect(() => {
    const verify = async () => {
      const savedToken = localStorage.getItem(TOKEN_KEY);
      if (!savedToken) { setLoading(false); return; }

      try {
        const res = await authService.me();
        const freshUser = res?.data || res;
        if (freshUser?.id) {
          const normalized = { ...freshUser, role: normalizeRole(freshUser.role) };
          setUser(normalized);
          localStorage.setItem(USER_KEY, JSON.stringify(normalized));
        }
      } catch {
        // Token invalid or expired — clear session
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, []);

  const login = useCallback((userData, authToken) => {
    const normalized = { ...userData, role: normalizeRole(userData.role) };
    localStorage.setItem(TOKEN_KEY, authToken);
    localStorage.setItem(USER_KEY, JSON.stringify(normalized));
    setUser(normalized);
    setToken(authToken);
  }, []);

  const logout = useCallback(async () => {
    try { await authService.logout(); } catch { /* ignore network errors on logout */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setToken(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const role          = normalizeRole(user?.role);
  const isSuperAdmin  = role === 'super_admin';
  const isSchoolAdmin = role === 'school_admin';
  const isTeacher     = role === 'teacher';
  const isAuthenticated = !!token && !!user;
  const mustChangePassword = !!user?.must_change_password;

  return (
    <AuthContext.Provider value={{
      user, token, loading, isAuthenticated,
      role, isSuperAdmin, isSchoolAdmin, isTeacher,
      mustChangePassword,
      login, logout, updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
